import { Ban, Check, Plus } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router';

import { WorkoutCalculatedMetricDefinitionDto } from '@api/FitnessActivityClient';
import { PageHeader } from '@components/page-header';
import { CellProps, ReactColumn, TableComponent } from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { links } from '@core/router';
import { formatEnumLabel } from '@core/utils/format-enum-label';

import { DeleteCalculatedMetricDefinitionDialog } from './delete-calculated-metric-definition-dialog';
import { useCalculatedMetricDefinitionsStore } from './list-store';

const SHOWN_WORKOUT_TYPES = 4;

const columns: ReactColumn<WorkoutCalculatedMetricDefinitionDto>[] = [
    {
        Header: 'Metric Key',
        id: 'metricKey',
        disableSortBy: true,
        accessor: 'metricKey',
        Cell: ({
            row: { original }
        }: CellProps<WorkoutCalculatedMetricDefinitionDto>) => (
            <span className="text-sm font-bold">{original.metricKey}</span>
        )
    },
    {
        Header: 'Label',
        id: 'label',
        disableSortBy: true,
        accessor: 'label'
    },
    {
        Header: 'Algorithm',
        id: 'algorithm',
        disableSortBy: true,
        accessor: 'algorithm',
        Cell: ({
            row: { original }
        }: CellProps<WorkoutCalculatedMetricDefinitionDto>) => (
            <span className="text-sm">
                {formatEnumLabel(original.algorithm)}
            </span>
        )
    },
    {
        Header: 'Distance',
        id: 'distanceMeters',
        disableSortBy: true,
        Cell: ({
            row: { original }
        }: CellProps<WorkoutCalculatedMetricDefinitionDto>) => (
            <span className="text-sm">
                {original.parameters.distanceMeters === undefined
                    ? '-'
                    : `${original.parameters.distanceMeters.toLocaleString(
                          'en-GB'
                      )} m`}
            </span>
        )
    },
    {
        Header: 'Workout Types',
        id: 'workoutTypes',
        disableSortBy: true,
        Cell: ({
            row: { original }
        }: CellProps<WorkoutCalculatedMetricDefinitionDto>) => (
            <div className="flex flex-wrap gap-1">
                {original.workoutTypes
                    .slice(0, SHOWN_WORKOUT_TYPES)
                    .map((type) => (
                        <Badge key={type} variant="outline">
                            {formatEnumLabel(type)}
                        </Badge>
                    ))}
                {original.workoutTypes.length > SHOWN_WORKOUT_TYPES && (
                    <Badge variant="muted">
                        {`+${original.workoutTypes.length - SHOWN_WORKOUT_TYPES}`}
                    </Badge>
                )}
            </div>
        )
    },
    {
        Header: 'Enabled',
        id: 'enabled',
        disableSortBy: true,
        Cell: ({
            row: { original }
        }: CellProps<WorkoutCalculatedMetricDefinitionDto>) => (
            <div className="flex items-center gap-1.5">
                {original.enabled ? (
                    <Check className="size-4 text-success" />
                ) : (
                    <Ban className="size-4 text-muted-foreground" />
                )}
                <span className="text-sm">
                    {original.enabled ? 'Enabled' : 'Disabled'}
                </span>
            </div>
        )
    }
];

export const CalculatedMetricDefinitions = () => {
    const navigate = useNavigate();
    const { definitions, refresh } = useCalculatedMetricDefinitionsStore();
    const [deleteTarget, setDeleteTarget] =
        React.useState<WorkoutCalculatedMetricDefinitionDto | null>(null);

    return (
        <>
            <PageHeader
                title="Calculated Metrics"
                actions={
                    <Button
                        onClick={() =>
                            navigate(links.createCalculatedMetricDefinition())
                        }
                    >
                        <Plus />
                        Create Metric
                    </Button>
                }
            />

            <TableComponent
                items={definitions}
                columns={columns}
                emptyText="No calculated metrics yet."
                contextMenu={[
                    {
                        label: 'Edit',
                        action: (row) =>
                            navigate(
                                links.editCalculatedMetricDefinition(
                                    String(row.id)
                                )
                            )
                    },
                    {
                        label: 'Delete',
                        action: (row) => setDeleteTarget(row)
                    }
                ]}
            />

            <DeleteCalculatedMetricDefinitionDialog
                definition={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onSuccess={() => {
                    setDeleteTarget(null);
                    refresh();
                }}
            />
        </>
    );
};
